import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import VideoCard, { VideoListItem } from "@/components/VideoCard";
import VideoModal from "@/components/VideoModal";
import { Search, Leaf, Download, ShoppingBag, Loader2, LayoutGrid, List } from "lucide-react";

const GUIDE_URL = `${API}/library/guide-ancestral-herbal/download`;

function GuideDownloadDialog({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const triggerDownload = () => {
    const a = document.createElement("a");
    a.href = GUIDE_URL;
    a.setAttribute("download", "Ancestral-Herbal-Starter-Guide.pdf");
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email, name: name || null });
      toast.success("You're subscribed — your guide is downloading. Ase!");
      triggerDownload();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Please try again.");
    } finally { setBusy(false); }
  };

  const field = "w-full bg-black/50 border border-white/10 px-4 py-3 text-bone focus:outline-none focus:border-gold/50";
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-surface border-white/10 text-bone" data-testid="guide-download-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Get Your Free Guide</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-bone/60 text-sm">Enter your email and we'll send you the Ancestral Herbal Starter Guide — and add you to the Sacred Dispatch for more free teachings.</p>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className={field} data-testid="guide-name" />
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className={field} data-testid="guide-email" />
          <button type="submit" disabled={busy} data-testid="guide-submit" className="w-full bg-gold text-black py-3 font-medium hover:bg-gold2 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Download the Guide
          </button>
          <button type="button" onClick={() => { triggerDownload(); onClose(); }} className="w-full text-bone/40 text-xs hover:text-bone/70 transition-colors" data-testid="guide-skip">
            No thanks, just download
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Videos() {
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [videos, setVideos] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem("videoView") || "panel");
  const [sort, setSort] = useState(() => localStorage.getItem("videoSort") || "views");
  const category = params.get("category") || "";
  const searchParam = params.get("search") || "";

  useEffect(() => { localStorage.setItem("videoView", view); }, [view]);
  useEffect(() => { localStorage.setItem("videoSort", sort); }, [sort]);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { setSearch(searchParam); }, [searchParam]);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (category) q.set("category", category);
    if (search) q.set("search", search);
    api.get(`/videos?${q.toString()}`).then((r) => { setVideos(r.data); setLoading(false); });
  }, [category, search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const setCat = (slug) => { slug ? setParams({ category: slug }) : setParams({}); };
  const play = (v) => { setActive(v); setOpen(true); };
  const activeCat = cats.find((c) => c.slug === category);

  const sortedVideos = useMemo(() => {
    const arr = [...videos];
    if (sort === "views") arr.sort((a, b) => (b.views || 0) - (a.views || 0));
    else if (sort === "newest") arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else if (sort === "az") arr.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    return arr;
  }, [videos, sort]);

  return (
    <main className="bg-onyx min-h-screen pt-16" data-testid="videos-page">
      <div className="border-b border-white/10 grain bg-surface">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Video Library</p>
          <h1 className="font-serif text-4xl md:text-6xl text-bone mb-4">{activeCat ? activeCat.name : "All Teachings"}</h1>
          <p className="text-bone/55 max-w-2xl text-lg">{activeCat ? activeCat.blurb : "Browse every lecture across all nine streams of knowledge."}</p>
          <div className="mt-8 relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-bone/40" size={18} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search teachers or topics..."
              data-testid="video-search-input"
              className="w-full bg-black/50 border border-white/10 pl-12 pr-4 py-3 text-bone placeholder:text-bone/30 focus:outline-none focus:border-gold/50" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setCat("")} data-testid="filter-all"
              className={`px-4 py-2 text-sm transition-colors ${!category ? "bg-gold text-black" : "border border-white/15 text-bone/70 hover:border-gold/40"}`}>All</button>
            {cats.map((c) => (
              <button key={c.slug} onClick={() => setCat(c.slug)} data-testid={`filter-${c.slug}`}
                className={`px-4 py-2 text-sm transition-colors ${category === c.slug ? "bg-gold text-black" : "border border-white/15 text-bone/70 hover:border-gold/40"}`}>
                {c.name.split(" ")[0]} <span className="opacity-50">{c.video_count}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 ml-auto">
            {!loading && <span className="text-bone/40 text-xs whitespace-nowrap" data-testid="video-count">{videos.length} teaching{videos.length === 1 ? "" : "s"}</span>}
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="video-sort"
              aria-label="Sort videos"
              className="bg-black/50 border border-white/15 text-bone/80 text-sm px-3 py-2 focus:outline-none focus:border-gold/50 cursor-pointer">
              <option value="views">Most viewed</option>
              <option value="newest">Newest</option>
              <option value="az">A–Z</option>
            </select>
            <div className="flex border border-white/15" role="group" aria-label="View mode">
              <button onClick={() => setView("panel")} data-testid="view-panel" aria-pressed={view === "panel"} title="Panel view"
                className={`p-2 transition-colors ${view === "panel" ? "bg-gold text-black" : "text-bone/60 hover:text-gold"}`}><LayoutGrid size={17} /></button>
              <button onClick={() => setView("list")} data-testid="view-list" aria-pressed={view === "list"} title="List view"
                className={`p-2 transition-colors border-l border-white/15 ${view === "list" ? "bg-gold text-black" : "text-bone/60 hover:text-gold"}`}><List size={17} /></button>
            </div>
          </div>
        </div>

        {category === "herbal" && (
          <div className="mb-10 border border-green-500/30 bg-gradient-to-r from-green-950/60 to-surface p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6" data-testid="herbal-banner">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.25em] text-gold mb-2 flex items-center gap-2"><Leaf size={15} /> Start Here</p>
              <h3 className="font-serif text-2xl text-bone mb-1">Ancestral Herbal Starter Guide</h3>
              <p className="text-bone/60 text-sm max-w-xl">A free beginner's PDF — folk remedies, preventative wellness, how to naturally source your herbs, and essential primitive skills. Then source your ingredients from trusted providers.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setShowGuide(true)} data-testid="herbal-guide-download"
                className="bg-gold text-black px-5 py-2.5 font-medium hover:bg-gold2 transition-colors flex items-center gap-2 whitespace-nowrap">
                <Download size={16} /> Download Guide
              </button>
              <Link to="/store" data-testid="herbal-shop-link"
                className="border border-white/20 text-bone px-5 py-2.5 hover:bg-white/5 hover:border-gold/40 transition-colors flex items-center gap-2 whitespace-nowrap">
                <ShoppingBag size={16} /> Shop Ingredients
              </Link>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-bone/40 py-20 text-center">Loading teachings...</p>
        ) : videos.length === 0 ? (
          <p className="text-bone/40 py-20 text-center" data-testid="no-videos">No lectures found.</p>
        ) : view === "list" ? (
          <div className="flex flex-col gap-3" data-testid="videos-list">
            {sortedVideos.map((v) => <VideoListItem key={v.id} video={v} onPlay={play} />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="videos-grid">
            {sortedVideos.map((v) => <VideoCard key={v.id} video={v} onPlay={play} />)}
          </div>
        )}
      </div>

      <VideoModal video={active} open={open} onClose={setOpen} />
      <GuideDownloadDialog open={showGuide} onClose={() => setShowGuide(false)} />
    </main>
  );
}
